import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import Pagination from 'rsuite/Pagination';
import MyLoader from '../../common/loader.component';
import { OutletContextParams } from './delivery-orders-screen';
import { usePaginationQs } from '../../../utils/usePagination';
import { useSearchParamsPaginationEffect } from '../purchase-order/useSearchParamsPaginationEffect';
import {
  CleanConversation,
  CleanSupplierWarehouse,
  CleanTransportation,
} from '../../../export-types/cleaned-types';
import { useConversationNewMessages } from '../../../api/conversation';
import { TransportationTabItem } from './transportation-tab-item';
import { useTransportations } from '../../../api/transportations';
import { ExpandButton } from '../../row';

type ItemWithWarehouse = {
  warehouse: CleanSupplierWarehouse;
  items: CleanTransportation[];
};

type TransportationsTabProps = {
  type?: 'standalone' | 'combined';
  status: 'created' | 'sent' | 'received' | 'packagedCombined';
};

export const TransportationsTabDefault = (props: TransportationsTabProps) => {
  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(15);

  useSearchParamsPaginationEffect(handleSetPage);
  const {
    debouncedInternalId,
    linked,
    attention,
    expanded,
    collapsed,
    onChangeCollapsed,
    selectSupplier,
    direction,
  } = useOutletContext<OutletContextParams>();
  const [collapsedStorage, setCollapsedStorage] = useState<string[]>([]);

  const sort = (() => {
    switch (props.status) {
      case 'created':
        return 'createdAt';
      case 'packagedCombined':
        return 'createdAt';
      case 'sent':
        return 'sentAt';
      case 'received':
        return 'receivedAt';
    }
  })();

  const { data, error, isLoading, mutate } = useTransportations({
    linked,
    attention,
    types: props.type ? [props.type] : undefined,
    status: [props.status],
    take,
    skip,
    name: debouncedInternalId || undefined,
    supplierId: selectSupplier || undefined,
    direction,
    sort,
  });

  const newMessageHandler = React.useCallback(
    (updatedConversation: CleanConversation) => {
      if (
        data?.items.some((transportation) =>
          transportation.lines.some(
            (line) =>
              line.deliveryOrder.id === updatedConversation.deliveryOrder?.id,
          ),
        )
      ) {
        mutate();
      }
    },
    [data, mutate],
  );
  useConversationNewMessages(newMessageHandler, 'deliveries');

  const onToggle = (transportation: CleanTransportation) => {
    if (collapsed.includes(transportation.id)) {
      const newCollapsed = [...collapsed];
      newCollapsed.splice(newCollapsed.indexOf(transportation.id), 1);
      onChangeCollapsed(newCollapsed);
    } else {
      onChangeCollapsed([...collapsed, transportation.id]);
    }
  };

  const { itemsWithWarehouse, itemsWithoutWarehouse } = useMemo<{
    itemsWithWarehouse: ItemWithWarehouse[];
    itemsWithoutWarehouse: CleanTransportation[];
  }>(() => {
    const itemsWithWarehouse: ItemWithWarehouse[] = [];
    const itemsWithoutWarehouse: CleanTransportation[] = [];
    if (props.type === 'standalone') {
      data?.items.forEach((item) => {
        if (!item.lines.length) {
          return;
        }
        const supplierWarehouse = item.lines[0].deliveryOrder.purchaseOrders
          ?.length
          ? item.lines[0].deliveryOrder.purchaseOrders[0].supplierWarehouse
          : null;

        if (!supplierWarehouse) {
          itemsWithoutWarehouse.push(item);
          return;
        }

        const index = itemsWithWarehouse.findIndex(
          (item) => item.warehouse.id === supplierWarehouse.id,
        );

        if (index === -1) {
          itemsWithWarehouse.push({
            warehouse: supplierWarehouse,
            items: [item],
          });
        } else {
          itemsWithWarehouse[index].items.push(item);
        }
      });
    }
    return { itemsWithWarehouse, itemsWithoutWarehouse };
  }, [data?.items]);

  const onToggleStorage = (storage: string) => {
    if (collapsedStorage.includes(storage)) {
      const newCollapsed = [...collapsedStorage];
      newCollapsed.splice(newCollapsed.indexOf(storage), 1);
      setCollapsedStorage(newCollapsed);
    } else {
      setCollapsedStorage([...collapsedStorage, storage]);
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="pb-4">
          <MyLoader />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <div className="pb-4">Неизвестная ошибка</div>
      </>
    );
  }

  const status = (() => {
    switch (props.status) {
      case 'created':
        return 'assembly';
      case 'packagedCombined':
        return 'assembly';
      case 'sent':
        return 'sentToRussia';
      case 'received':
        return 'sentToRussia';
    }
  })();

  return (
    <>
      <div className="pb-4">
        {itemsWithWarehouse.length || itemsWithoutWarehouse.length ? (
          <>
            {itemsWithWarehouse.map((item, index) => {
              return (
                <div key={index} className={'border-bottom mb-3'}>
                  <div className={'d-flex flex-row gap-2 fs-21 '}>
                    {item.warehouse.location?.name}
                    <div className={'mleft-auto'}>
                      <ExpandButton
                        onToggle={() =>
                          onToggleStorage(item.warehouse.id.toString())
                        }
                        collapsed={collapsedStorage.includes(
                          item.warehouse.id.toString(),
                        )}
                      />
                    </div>
                  </div>

                  {!collapsedStorage.includes(item.warehouse.id.toString()) &&
                    item.items.map((data) => (
                      <TransportationTabItem
                        key={data.id}
                        data={data}
                        onCollapsedChange={() => {
                          onToggle(data);
                        }}
                        collapsed={
                          (!expanded && collapsed.includes(data.id)) ||
                          (expanded && !collapsed.includes(data.id))
                        }
                        status={status}
                      />
                    ))}
                </div>
              );
            })}
            {itemsWithoutWarehouse.map((data) => (
              <TransportationTabItem
                key={data.id}
                data={data}
                onCollapsedChange={() => {
                  onToggle(data);
                }}
                collapsed={
                  (!expanded && collapsed.includes(data.id)) ||
                  (expanded && !collapsed.includes(data.id))
                }
                status={status}
              />
            ))}
          </>
        ) : (
          data.items.map((data) => (
            <TransportationTabItem
              key={data.id}
              data={data}
              onCollapsedChange={() => {
                onToggle(data);
              }}
              collapsed={
                (!expanded && collapsed.includes(data.id)) ||
                (expanded && !collapsed.includes(data.id))
              }
              status={status}
            />
          ))
        )}
      </div>
      {data.total > take && (
        <div className="pb-4">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="md"
            layout={['pager']}
            total={data?.total}
            limitOptions={[15, 20]}
            limit={take}
            activePage={page}
            onChangePage={handleSetPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      )}
    </>
  );
};
